import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { MyCompaniesDocument } from "@/graphql/generated/operations";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { userErrorMessage } from "@/lib/user-error";
import { COMPANY_ROLE_ADMIN, COMPANY_ROLE_MEMBER } from "./company-roles";
import { CompanyLogo } from "./CompanyLogo";
import { useCompanyContext } from "./use-company-context";

// "My companies" at /c — the caller's own memberships, and the way into a company that already
// exists without knowing its id (the company account model, which has the person
// see the companies behind her `company_membership` rows).
//
// THIS IS THE SWITCHER §5 SPECIFIES: "die Person sieht ihre Firmen (aus ihren
// `company_membership`-Zeilen) und schaltet in eine hinein." The list is the seeing; the control on
// each row is the switching. It supersedes @external-adr GENESIS-0025-no-company-context-switch-in-the-mvp
// (genesis), which had declined to build it — see that ADR's superseded header for the reversal.
//
// EVERY MEMBERSHIP IS LISTED; ONLY AN ADMIN GETS THE SWITCH. §4 says of a plain member that "die
// Firma taucht in seinem Kontext-Switch auf", so a membership is enough to appear here — and until
// concept v10 it was enough to ENTER as well. v10 narrowed that: every capability the context has is
// admin-only, so for a member it was a mode in which nothing worked, and in the composer it was worse
// than useless. A member therefore keeps the row, the page and the listing, and does not get the
// button (0002 §5, v10). genesis gates each per-company write on an `admin` membership row anyway
// (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization), so this is presentation, not authorisation.
//
// Reading one's own memberships discloses nothing about anyone else, so genesis gates it on nothing
// beyond being authenticated — and the §7 publication rule that hides an unshared member from a
// stranger deliberately does not apply to the subject reading their own list.

export function MyCompaniesPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  // Reads the SAME `myCompanies` answer this page renders (Apollo dedupes the two), so the row a
  // person can enter and the context that results are always the same server-verified row.
  const { active, enter } = useCompanyContext();
  // Level A, the ONLY thing the platform role does (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §1a). It says nothing about
  // administering a company that already exists — that is the membership row, per row, and is why
  // this gate never touches the list below it.
  const canCreateCompany = useHasPermission(Permission.CreateCompany);
  const { data, loading, error } = useQuery(MyCompaniesDocument);

  const memberships = data?.myCompanies.memberships ?? [];
  // View state only (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): how many companies the
  // list shows. Which they are, and the roles, the assistant reads from MyCompanies itself.
  useAssistantContext(
    data
      ? [
          {
            description: "the user's own companies page",
            value: `${memberships.length} companies listed`,
          },
        ]
      : null,
  );
  // cache-and-network is the app-wide policy, so bare `loading` would repaint a loading line over
  // perfectly good rows on every revisit.
  const firstLoad = loading && !data;

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="MyCompanies"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 id="companies-heading" className="smycles-page-title text-ink">
            <FormattedMessage id="company.mine.title" defaultMessage="My companies" />
          </h1>
          <p className="smycles-field-meta mt-1 text-ink-muted">
            <FormattedMessage
              id="company.mine.intro"
              defaultMessage="The companies you are listed at. Open one to see its page."
            />
          </p>
        </div>
        {/* Same gate as the /c/new route guard and the account menu entry, and presentation only in
            all three: genesis re-reads the role from the token when createCompany runs. */}
        {canCreateCompany ? (
          <Link id="companies-new" to="/c/new" className={buttonClasses({ size: "compact" })}>
            <FormattedMessage id="company.mine.new" defaultMessage="New company" />
          </Link>
        ) : null}
      </div>

      <div className="mt-6 max-w-2xl">
        {firstLoad ? (
          <p className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="company.mine.loading" defaultMessage="Loading your companies" />
          </p>
        ) : error ? (
          <p className="smycles-field-meta text-ink">{userErrorMessage(intl, error)}</p>
        ) : memberships.length === 0 ? (
          // Neutral by convention: having no company is an ordinary state, not a failure, so it gets
          // the same plain note every other empty section on the company page gets — no ring, no
          // colour, nothing to read as a warning.
          <p id="companies-empty" className="smycles-field-meta text-ink-muted">
            {canCreateCompany ? (
              <FormattedMessage
                id="company.mine.emptyWithCreate"
                defaultMessage="You are not listed at any company yet. Register one to get started."
              />
            ) : (
              <FormattedMessage
                id="company.mine.empty"
                defaultMessage="You are not listed at any company yet. An administrator of a company can add you to it."
              />
            )}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {memberships.map((membership) => {
              const company = membership.company;
              const industryName =
                company.industry?.localizedName?.localized ?? company.industry?.name ?? null;
              const isActive = active?.id === company.id;
              // ADMIN ONLY since concept v10 (0002 §5). Also decides the row's right padding: the
              // switch needs room between it and the row's edge, and a row without one does not.
              const canActAsCompany = membership.company.canAdminister;
              return (
                <li
                  key={company.id}
                  id={`companies-row-${company.id}`}
                  // THE HOVER IS THE ROW'S, not the link's. The switch sits outside the link — a row
                  // must never be a button inside a link — so a hover on the link left the strip
                  // carrying that switch at the row's own background: the row lit up except for its
                  // right-hand end, which read as two elements rather than one. Moving it here also
                  // means pointing at the switch highlights the row it belongs to.
                  className={`flex items-center gap-2 rounded-md bg-ink/[0.02] transition-colors hover:bg-ink/[0.06] ${
                    canActAsCompany ? "pr-3" : ""
                  }`}
                >
                  <Link
                    to={`/c/${company.id}`}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-md p-3"
                  >
                    {/* The real logo, and the monogram only for a company that has not uploaded
                        one. THE SAME COMPONENT THE COMPANY PAGE DRAWS, at the row rung: the
                        fallback is a decision of The company page, and
                        a second copy of it here is a second place for it to drift.

                        This list may ask for the logo where a member roster may not: genesis
                        delivers the field on `myCompanies` because it is bounded by the caller's own
                        memberships, so the "one base64 image per row" cost the rule guards against
                        does not arise. See the note on the operation. */}
                    <CompanyLogo logo={company.logo} name={company.name} size="row" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-smycles-body text-ink">{company.name}</span>
                      {industryName ? (
                        <span className="smycles-field-meta block truncate text-ink-muted">{industryName}</span>
                      ) : null}
                    </span>
                    {/* The role, as a plain word. Only the two the MVP vocabulary fixes are labelled:
                        `role` is an open string, and a later one would otherwise reach the user as a
                        raw genesis value with no translation. */}
                    {membership.role === COMPANY_ROLE_ADMIN ||
                    membership.role === COMPANY_ROLE_MEMBER ? (
                      <span className="smycles-field-meta flex-shrink-0 text-ink-muted">
                        {membership.role === COMPANY_ROLE_ADMIN ? (
                          <FormattedMessage
                            id="company.admin.members.roleAdmin"
                            defaultMessage="Administrator"
                          />
                        ) : (
                          <FormattedMessage
                            id="company.admin.members.roleMember"
                            defaultMessage="Member"
                          />
                        )}
                      </span>
                    ) : null}
                  </Link>
                  {/* The switch, outside the link so a row is never a button inside a link. The
                      company already being acted as states that in words and offers no second
                      control: leaving lives in the bar, which is on screen for as long as the mode
                      is. Neutral either way — a chip and a sentence, no ring and no colour. */}
                  {/* A member belongs to the company and sees it here, but cannot act as it: every
                      capability the context has is admin-only, so the mode did nothing for them.
                      Absent rather than disabled — a greyed-out button promises a right that does
                      not exist. */}
                  {!canActAsCompany ? null : isActive ? (
                    <span className="smycles-field-meta flex-shrink-0 text-ink-muted">
                      <FormattedMessage
                        id="company.context.current"
                        defaultMessage="Acting as this company"
                      />
                    </span>
                  ) : (
                    <Button
                      variant="chip"
                      size="sm"
                      className="flex-shrink-0"
                      // Every row's button reads identically otherwise, so out of context the list
                      // is N identical controls. The visible chip stays short; the accessible name
                      // says which company it enters.
                      aria-label={intl.formatMessage(
                        { id: "company.context.enterNamed", defaultMessage: "Act as {name}" },
                        { name: company.name },
                      )}
                      onClick={() => {
                        enter(company.id);
                        // Into the company, not merely into the mode: §6 confines what the context
                        // may do to the company's own page, so that is where entering leads.
                        void navigate(`/c/${company.id}`);
                      }}
                    >
                      <FormattedMessage
                        id="company.context.enter"
                        defaultMessage="Act as this company"
                      />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
